import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { Banner, Button, Combobox, RequiredMark, TextField } from '../../../ds/components';
import { useStableId } from '../../../ds/hooks';
import { ApiError, errorMessage } from '../../../lib/api';
import { createDeveloper, useDevelopers } from '../api';
import type { Developer } from '../types';
import styles from './DeveloperPicker.module.css';

export interface DeveloperPickerProps {
  value: Pick<Developer, 'id' | 'name' | 'initials'> | null;
  onChange: (developer: Pick<Developer, 'id' | 'name' | 'initials'> | null) => void;
  error?: string | undefined;
  disabled?: boolean | undefined;
  /** Objekt anlegen 01: „Verkäufer / Bauträger *" with the hint under the field. */
  label?: string | undefined;
  hint?: string | undefined;
  required?: boolean | undefined;
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

/**
 * „Verkäufer / Bauträger" (Objekt anlegen 01): the selected developer as a chip with the remove cross
 * and „aus Kontakten", otherwise a combobox over `GET /developers?q=` with the row „Neuen Bauträger
 * anlegen", which opens the inline form for `POST /developers`.
 */
export function DeveloperPicker({
  value,
  onChange,
  error,
  disabled,
  label = 'Verkäufer / Bauträger',
  hint = 'Verkäufer und Bauträger sind eine Partei.',
  required = true,
}: DeveloperPickerProps) {
  const id = useStableId();
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState<{ name: string } | null>(null);
  const developers = useDevelopers(query.trim(), !disabled && value === null);
  const options = useMemo(
    () =>
      (developers.data?.items ?? []).map((developer) => ({
        id: developer.id,
        label: developer.name,
        description: [developer.contactPerson, developer.city].filter(Boolean).join(' · ') || undefined,
      })),
    [developers.data],
  );

  if (value) {
    return (
      <div className={styles.wrapper}>
        <span className={styles.label} id={`${id}-label`}>
          {label}
          {required ? <RequiredMark /> : null}
        </span>
        <div className={styles.chipField} aria-labelledby={`${id}-label`}>
          <span className={styles.chip}>
            <span className={styles.chipAvatar} aria-hidden="true">
              {value.initials || initialsOf(value.name)}
            </span>
            <span className={styles.chipName}>{value.name}</span>
            {!disabled ? (
              <button
                type="button"
                className={styles.chipRemove}
                aria-label={`${value.name} entfernen`}
                onClick={() => onChange(null)}
              >
                <X size={12} strokeWidth={1.75} aria-hidden="true" />
              </button>
            ) : null}
          </span>
          <span className={styles.source}>aus Kontakten</span>
        </div>
        {error ? (
          <span className={styles.error} role="alert">
            {error}
          </span>
        ) : hint ? (
          <span className={styles.hint}>{hint}</span>
        ) : null}
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <Combobox
        label={label}
        required={required}
        options={options}
        value={null}
        filter={false}
        placeholder="Bauträger suchen"
        emptyText={developers.isPending && query.trim() ? 'Suche …' : 'Keine Treffer'}
        onQueryChange={setQuery}
        onChange={(_id, option) => {
          if (!option) return;
          const developer = developers.data?.items.find((entry) => entry.id === option.id);
          onChange(
            developer ? { id: developer.id, name: developer.name, initials: developer.initials } : null,
          );
        }}
        onCreate={(text) => setCreating({ name: text })}
        createLabel={(text) => (text ? `Neuen Bauträger anlegen: „${text}“` : 'Neuen Bauträger anlegen')}
        hint={hint}
        error={error}
        disabled={disabled}
      />
      {creating ? (
        <CreateDeveloperForm
          initialName={creating.name}
          onCancel={() => setCreating(null)}
          onCreated={(developer) => {
            setCreating(null);
            onChange({ id: developer.id, name: developer.name, initials: developer.initials });
          }}
        />
      ) : null}
    </div>
  );
}

function CreateDeveloperForm({
  initialName,
  onCancel,
  onCreated,
}: {
  initialName: string;
  onCancel: () => void;
  onCreated: (developer: Developer) => void;
}) {
  const [name, setName] = useState(initialName);
  const [contactPerson, setContactPerson] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    setName(initialName);
  }, [initialName]);

  async function submit() {
    setFields({});
    setError(null);
    if (!name.trim()) {
      setFields({ name: 'Bitte einen Namen angeben.' });
      return;
    }
    setPending(true);
    try {
      const developer = await createDeveloper({
        name: name.trim(),
        contactPerson: contactPerson.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
      });
      onCreated(developer);
    } catch (err) {
      if (err instanceof ApiError && Object.keys(err.fields).length > 0) setFields(err.fields);
      else setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className={styles.createForm} role="group" aria-label="Neuen Bauträger anlegen">
      <div className={styles.createTitle}>Neuen Bauträger anlegen</div>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <TextField
        label="Name"
        required
        value={name}
        error={fields['name']}
        onChange={(event) => setName(event.target.value)}
        autoFocus
      />
      <div className={styles.createGrid}>
        <TextField
          label="Ansprechpartner"
          optional
          value={contactPerson}
          onChange={(event) => setContactPerson(event.target.value)}
        />
        <TextField
          label="E-Mail"
          type="email"
          optional
          value={email}
          error={fields['email']}
          hint="Für den Upload-Link des Bauträgers."
          onChange={(event) => setEmail(event.target.value)}
        />
        <TextField
          label="Telefon"
          type="tel"
          optional
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
        />
      </div>
      <div className={styles.createActions}>
        <Button variant="secondary" size="small" onClick={onCancel} disabled={pending}>
          Abbrechen
        </Button>
        <Button size="small" loading={pending} onClick={() => void submit()}>
          Bauträger anlegen
        </Button>
      </div>
    </div>
  );
}
